// socketService.ts - HMR-Safe Version
import { io, Socket } from "socket.io-client";
import {
  setSocketConnected,
  setSocketInitialized,
} from "../../store/slices/misc/socketSlice";
import { store } from "../../store/store";
import Cookies from "js-cookie";

// Global state that persists across HMR reloads
const GLOBAL_STATE_KEY = "__SOCKET_SERVICE_STATE__";

interface GlobalSocketState {
  socket: Socket | null;
  reconnectTimeout: NodeJS.Timeout | null;
  isInitializing: boolean;
  isDestroyed: boolean;
  totalReconnectAttempts: number;
  lastConnectionTime: number;
}

// Get or create global state
const getGlobalState = (): GlobalSocketState => {
  if (!(window as any)[GLOBAL_STATE_KEY]) {
    (window as any)[GLOBAL_STATE_KEY] = {
      socket: null,
      reconnectTimeout: null,
      isInitializing: false,
      isDestroyed: false,
      totalReconnectAttempts: 0,
      lastConnectionTime: 0,
    };
  }
  return (window as any)[GLOBAL_STATE_KEY];
};

// Clean up previous instance on HMR
const cleanupPreviousInstance = () => {
  const state = getGlobalState();

  if (state.reconnectTimeout) {
    console.log("Cleaning up previous reconnection timeout");
    clearTimeout(state.reconnectTimeout);
    state.reconnectTimeout = null;
  }

  // Don't disconnect the socket, just clean up timers
  state.isInitializing = false;
};

// Call cleanup immediately on module load
cleanupPreviousInstance();

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
console.log("SOCKET_URL", SOCKET_URL);
const isHttps =
  SOCKET_URL?.startsWith("https://") || window.location.protocol === "https:";
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_INTERVAL = 5000;
const MAX_TOTAL_RECONNECT_ATTEMPTS = 8;
const CONNECTION_COOLDOWN = 3000; // Minimum time between connection attempts

// Track active room operations to prevent duplicates
const activeRoomOperations = new Map<string, Promise<boolean>>();

// Main socket initialization function
export const initializeSocket = (
  userId: string,
  clientId: string,
  token: string,
  onConnect?: () => void,
  onDisconnect?: () => void,
  onError?: (error: any) => void
): Socket => {
  const state = getGlobalState();

  if (state.isDestroyed) {
    console.log("Socket service is destroyed, not initializing");
    throw new Error("Socket service is destroyed");
  }

  if (state.isInitializing) {
    console.warn("Socket initialization already in progress");
    if (state.socket) return state.socket;
  }

  if (state.socket?.connected) {
    console.log("Socket already connected");
    return state.socket;
  }

  // Check cooldown period to prevent rapid reconnection attempts
  const now = Date.now();
  if (now - state.lastConnectionTime < CONNECTION_COOLDOWN) {
    console.log(
      `Connection cooldown active, waiting ${
        CONNECTION_COOLDOWN - (now - state.lastConnectionTime)
      }ms`
    );
    throw new Error("Connection cooldown active");
  }

  // Check if we've exceeded total attempts
  if (state.totalReconnectAttempts >= MAX_TOTAL_RECONNECT_ATTEMPTS) {
    console.error(
      "Maximum total reconnection attempts reached. Socket service destroyed."
    );
    state.isDestroyed = true;
    store.dispatch(setSocketConnected(false));
    store.dispatch(setSocketInitialized(false));
    throw new Error("Maximum reconnection attempts exceeded");
  }

  state.isInitializing = true;
  state.totalReconnectAttempts++;
  state.lastConnectionTime = now;

  try {
    if (state.socket) {
      state.socket.removeAllListeners();
      state.socket.disconnect();
    }

    console.log(
      `🔌 Initializing socket connection... (Attempt ${state.totalReconnectAttempts}/${MAX_TOTAL_RECONNECT_ATTEMPTS})`
    );

    state.socket = io(`${SOCKET_URL}`, {
      path: process.env.REACT_APP_SOCKET_PATH,
      query: { userId, clientId, token },
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 15000,
      transports: ["websocket"],
      secure: isHttps, // ← Auto-detect based on URL protocol
      rejectUnauthorized: true, // ← Enable certificate validation for production
      forceNew: true,
    });

    state.socket.on("connect", () => {
      console.log("✅ Socket connected with ID:", state.socket?.id);
      store.dispatch(setSocketConnected(true));
      store.dispatch(setSocketInitialized(true));
      state.isInitializing = false;

      // Reset attempts on successful connection
      state.totalReconnectAttempts = 0;
      state.isDestroyed = false;

      if (state.reconnectTimeout) {
        clearTimeout(state.reconnectTimeout);
        state.reconnectTimeout = null;
      }

      if (onConnect) onConnect();
    });

    state.socket.on("disconnect", (reason: any) => {
      console.log("❌ Socket disconnected:", reason);
      store.dispatch(setSocketConnected(false));
      state.isInitializing = false;

      if (reason === "io server disconnect" || reason === "transport close") {
        store.dispatch(setSocketInitialized(false));
      }

      if (onDisconnect) onDisconnect();

      // Only attempt reconnection for server disconnects and if not destroyed
      if (reason === "io server disconnect" && !state.isDestroyed) {
        console.log("🔄 Server disconnected socket, scheduling reconnect...");
        scheduleReconnect(
          userId,
          clientId,
          token,
          onConnect,
          onDisconnect,
          onError
        );
      }
    });

    state.socket.on("connect_error", (err: any) => {
      console.error("🚫 Socket connection error:", err.message);
      store.dispatch(setSocketConnected(false));
      state.isInitializing = false;

      if (onError) onError(err);
    });

    state.socket.on("reconnect_attempt", (attemptNumber: number) => {
      console.log(
        `🔄 Socket.IO reconnect attempt ${attemptNumber}/${MAX_RECONNECT_ATTEMPTS}`
      );
    });

    state.socket.on("reconnect", (attemptNumber: number) => {
      console.log(`✅ Socket.IO reconnected after ${attemptNumber} attempts`);
      store.dispatch(setSocketConnected(true));
      store.dispatch(setSocketInitialized(true));
      state.totalReconnectAttempts = 0;
      state.isDestroyed = false;
    });

    state.socket.on("reconnect_failed", () => {
      console.error("🚫 Socket.IO failed to reconnect after maximum attempts");
      store.dispatch(setSocketConnected(false));
      store.dispatch(setSocketInitialized(false));

      if (!state.isDestroyed) {
        console.log(
          "🔄 Scheduling manual reconnection after Socket.IO failure..."
        );
        scheduleReconnect(
          userId,
          clientId,
          token,
          onConnect,
          onDisconnect,
          onError
        );
      }
    });
  } catch (error) {
    console.error("💥 Error initializing socket:", error);
    state.isInitializing = false;
    if (onError) onError(error);
    throw error;
  }

  return state.socket;
};

// Schedule reconnection with checks
const scheduleReconnect = (
  userId: string,
  clientId: string,
  token: string,
  onConnect?: () => void,
  onDisconnect?: () => void,
  onError?: (error: any) => void
): void => {
  const state = getGlobalState();

  if (state.reconnectTimeout) {
    console.log("⏳ Reconnection already scheduled, skipping");
    return;
  }

  if (state.isDestroyed) {
    console.log("💀 Socket service is destroyed, not scheduling reconnection");
    return;
  }

  if (state.totalReconnectAttempts >= MAX_TOTAL_RECONNECT_ATTEMPTS) {
    console.error(
      "🛑 Maximum total reconnection attempts reached. No more attempts."
    );
    state.isDestroyed = true;
    store.dispatch(setSocketInitialized(false));
    return;
  }

  // Validate token before scheduling reconnection
  const tokenData = Cookies.get("access-token");
  if (!tokenData || !isTokenValid(tokenData)) {
    console.error("🔒 Cannot schedule reconnection - invalid or expired token");
    state.isDestroyed = true;
    store.dispatch(setSocketConnected(false));
    store.dispatch(setSocketInitialized(false));
    return;
  }

  console.log(
    `⏰ Scheduling reconnection in ${RECONNECT_INTERVAL}ms (Total attempts: ${state.totalReconnectAttempts}/${MAX_TOTAL_RECONNECT_ATTEMPTS})`
  );

  state.reconnectTimeout = setTimeout(() => {
    state.reconnectTimeout = null;

    if (state.isDestroyed) {
      console.log(
        "💀 Socket service destroyed during timeout, cancelling reconnection"
      );
      return;
    }

    // Re-validate token at reconnection time
    const currentTokenData = Cookies.get("access-token");
    if (!currentTokenData || !isTokenValid(currentTokenData)) {
      console.error(
        "🔒 Token invalid at reconnection time, stopping reconnection attempts"
      );
      state.isDestroyed = true;
      store.dispatch(setSocketConnected(false));
      store.dispatch(setSocketInitialized(false));
      return;
    }

    console.log("🚀 Executing scheduled reconnection with valid token...");

    try {
      const freshToken = JSON.parse(currentTokenData)?.access_token;
      if (freshToken) {
        initializeSocket(
          userId,
          clientId,
          freshToken,
          onConnect,
          onDisconnect,
          onError
        );
      }
    } catch (error) {
      console.error("💥 Scheduled reconnection failed:", error);
    }
  }, RECONNECT_INTERVAL);
};

// Token validation helper
const isTokenValid = (tokenData: string): boolean => {
  try {
    const parsedToken = JSON.parse(tokenData);
    const token = parsedToken?.access_token;
    const expiresAt = parsedToken?.expires_at;

    if (!token) {
      console.warn("🔒 Token missing access_token");
      return false;
    }

    // Check if token has expiry and if it's expired
    if (expiresAt) {
      const now = Date.now() / 1000; // Convert to seconds
      const expiry =
        typeof expiresAt === "string" ? parseInt(expiresAt) : expiresAt;

      if (now >= expiry) {
        console.warn("🔒 Token expired, cannot reconnect");
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("🔒 Token validation failed:", error);
    return false;
  }
};

// Get current socket instance with auto-initialization
export const getSocket = (): Socket => {
  const state = getGlobalState();

  if (state.isDestroyed) {
    throw new Error("Socket service is destroyed");
  }

  if (!state.socket) {
    const tokenData = Cookies.get("access-token");
    const clientId = sessionStorage.getItem("clientId");
    const currentUserId = store.getState()?.currentUser?.data?.id;

    if (tokenData && clientId && currentUserId) {
      // Validate token before attempting reconnection
      if (!isTokenValid(tokenData)) {
        console.error(
          "🔒 Invalid or expired token - cannot auto-initialize socket"
        );
        throw new Error("Invalid or expired token");
      }

      try {
        const token = JSON.parse(tokenData)?.access_token;
        if (token) {
          console.log("🔄 Auto-reinitializing socket with valid token...");
          return initializeSocket(currentUserId.toString(), clientId, token);
        }
      } catch (error) {
        console.error("💥 Failed to parse token for auto-reconnection:", error);
      }
    }
    throw new Error("Socket not initialized and cannot auto-initialize");
  }

  return state.socket;
};

// Disconnect and cleanup socket
export const disconnectSocket = (): void => {
  console.log("🧹 Disconnecting and cleaning up socket...");
  const state = getGlobalState();

  state.isDestroyed = true;

  if (state.reconnectTimeout) {
    clearTimeout(state.reconnectTimeout);
    state.reconnectTimeout = null;
  }

  if (state.socket) {
    state.socket.removeAllListeners();
    state.socket.disconnect();
    state.socket = null;
  }

  state.isInitializing = false;
  state.totalReconnectAttempts = 0;

  store.dispatch(setSocketConnected(false));
  store.dispatch(setSocketInitialized(false));
};

// Reset socket service (for complete restart)
export const resetSocketService = (): void => {
  console.log("🔄 Resetting socket service...");
  disconnectSocket();
  const state = getGlobalState();
  state.isDestroyed = false;
  state.totalReconnectAttempts = 0;
  state.lastConnectionTime = 0;
};

// Join a room - waits for connection
export const joinRoom = (module: string, id?: number): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const state = getGlobalState();

    if (state.isDestroyed) {
      reject(new Error("Socket service is destroyed"));
      return;
    }

    if (!id) {
      console.warn(
        `⚠️ Cannot join room - ID is undefined for module: ${module}`
      );
      reject(new Error(`Invalid room ID for module: ${module}`));
      return;
    }

    const roomName = `${module}_${id}`;

    // Check if operation is already in progress
    if (activeRoomOperations.has(roomName)) {
      console.log(
        `⏳ Join operation already in progress for room: ${roomName}`
      );
      activeRoomOperations.get(roomName)!.then(resolve).catch(reject);
      return;
    }

    // Create the join operation promise
    const joinOperation = new Promise<boolean>(async (resolveOp, rejectOp) => {
      try {
        console.log(`🚪 Attempting to join room: ${roomName}`);

        // Get socket instance
        let socketInstance: any;
        try {
          socketInstance = getSocket();
        } catch (error: any) {
          rejectOp(new Error(`Socket not available: ${error.message}`));
          return;
        }

        // Wait for socket to be connected if it's not already
        if (!socketInstance.connected) {
          console.log(`⏳ Socket not connected, waiting for connection...`);

          await new Promise<void>((connectResolve, connectReject) => {
            const timeout = setTimeout(() => {
              socketInstance.off("connect", onConnect);
              socketInstance.off("connect_error", onConnectError);
              connectReject(
                new Error(`Connection timeout for room: ${roomName}`)
              );
            }, 15000);

            const onConnect = () => {
              clearTimeout(timeout);
              socketInstance.off("connect", onConnect);
              socketInstance.off("connect_error", onConnectError);
              console.log(
                `✅ Socket connected, proceeding to join room: ${roomName}`
              );
              connectResolve();
            };

            const onConnectError = (err: any) => {
              clearTimeout(timeout);
              socketInstance.off("connect", onConnect);
              socketInstance.off("connect_error", onConnectError);
              connectReject(new Error(`Connection error: ${err.message}`));
            };

            if (socketInstance.connected) {
              clearTimeout(timeout);
              connectResolve();
              return;
            }

            socketInstance.once("connect", onConnect);
            socketInstance.once("connect_error", onConnectError);
          });
        }

        // Now join the room
        console.log(`🏠 Socket ready, joining room: ${roomName}`);

        const joinTimeout = setTimeout(() => {
          rejectOp(new Error(`Timeout joining room: ${roomName}`));
        }, 8000);

        socketInstance.emit("join-room", { module, id }, (ack: any) => {
          clearTimeout(joinTimeout);

          if (ack?.success) {
            console.log(`✅ Successfully joined room: ${roomName}`);
            resolveOp(true);
          } else {
            console.error(`🚫 Failed to join room: ${roomName}`, ack?.message);
            rejectOp(
              new Error(ack?.message || `Failed to join room: ${roomName}`)
            );
          }
        });
      } catch (error) {
        console.error(`💥 Error in join operation for ${roomName}:`, error);
        rejectOp(error);
      }
    });

    // Store and handle the operation
    activeRoomOperations.set(roomName, joinOperation);

    joinOperation
      .then(resolve)
      .catch(reject)
      .finally(() => {
        activeRoomOperations.delete(roomName);
      });
  });
};

// Leave a room - simple version
export const leaveRoom = (module: any, id?: number): Promise<boolean> => {
  return new Promise((resolve) => {
    const state = getGlobalState();

    if (!id) {
      console.warn(
        `⚠️ Cannot leave room - ID is undefined for module: ${module}`
      );
      resolve(true);
      return;
    }

    if (!state.socket?.connected || state.isDestroyed) {
      console.warn("⚠️ Socket not connected or destroyed - cannot leave room");
      resolve(true);
      return;
    }

    const roomName = `${module}_${id}`;
    console.log(`🚪 Leaving room: ${roomName}`);

    const timeoutId = setTimeout(() => {
      console.warn(`⏰ Timeout leaving room: ${roomName}`);
      resolve(true);
    }, 3000);

    state.socket.emit("leave-room", { module, id }, (ack: any) => {
      clearTimeout(timeoutId);

      if (ack?.success) {
        console.log(`✅ Successfully left room: ${roomName}`);
      } else {
        console.error(`🚫 Failed to leave room: ${roomName}`, ack?.message);
      }
      resolve(true);
    });
  });
};

// Get socket connection status
export const getSocketStatus = () => {
  const state = getGlobalState();
  return {
    exists: !!state.socket,
    connected: state.socket?.connected || false,
    id: state.socket?.id,
    isInitializing: state.isInitializing,
    isDestroyed: state.isDestroyed,
    totalReconnectAttempts: state.totalReconnectAttempts,
    maxTotalAttempts: MAX_TOTAL_RECONNECT_ATTEMPTS,
  };
};

// Force reconnection (with safety checks)
export const forceReconnect = (): void => {
  const state = getGlobalState();

  if (state.isDestroyed) {
    console.log("💀 Socket service is destroyed, cannot force reconnect");
    return;
  }

  if (state.totalReconnectAttempts >= MAX_TOTAL_RECONNECT_ATTEMPTS) {
    console.log(
      "🛑 Maximum reconnection attempts reached, cannot force reconnect"
    );
    return;
  }

  if (state.socket) {
    console.log("🔄 Forcing socket reconnection...");
    state.totalReconnectAttempts++;
    state.socket.disconnect();
    state.socket.connect();
  }
};

// HMR-safe auto-initialization
(() => {
  const state = getGlobalState();

  // Skip if already connected or if we've tried too many times
  if (
    state.socket?.connected ||
    state.isDestroyed ||
    state.totalReconnectAttempts >= MAX_TOTAL_RECONNECT_ATTEMPTS
  ) {
    console.log(
      "🔄 Skipping auto-initialization - socket exists or service destroyed"
    );
    return;
  }

  const tryAutoInitialize = () => {
    const tokenData = Cookies.get("access-token");
    const clientId = sessionStorage.getItem("clientId");
    const currentUser = store.getState()?.currentUser?.data;

    if (tokenData && clientId && currentUser?.id) {
      // Validate token before attempting auto-initialization
      if (!isTokenValid(tokenData)) {
        console.warn(
          "🔒 Skipping auto-initialization - invalid or expired token"
        );
        return;
      }

      try {
        const token = JSON.parse(tokenData)?.access_token;
        if (token) {
          console.log(
            "🚀 Auto-initializing socket on page load with valid token..."
          );
          initializeSocket(
            currentUser.id.toString(),
            clientId,
            token,
            () => console.log("✅ Auto-initialization successful"),
            () =>
              console.log("❌ Socket disconnected after auto-initialization"),
            (error) => console.error("💥 Auto-initialization failed:", error)
          );
        }
      } catch (error) {
        console.error("💥 Error during auto-initialization:", error);
      }
    }
  };

  // Try immediately if user data is available
  tryAutoInitialize();

  // Also listen for store changes (in case user data loads later)
  const unsubscribe = store.subscribe(() => {
    if (!state.socket?.connected && !state.isDestroyed) {
      tryAutoInitialize();
      unsubscribe(); // Only try once per module load
    }
  });

  // Cleanup on page unload
  window.addEventListener("beforeunload", () => {
    disconnectSocket();
  });
})();

// HMR cleanup
// if (module.hot) {
//   module.hot.dispose(() => {
//     cleanupPreviousInstance();
//   });
// }
